import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import SectionWrapper from './premium/SectionWrapper';
import { cities } from './cities-data';
import slugify from '../../utils/slugify';

// Hardcoded brand names — avoids importing the 8.9MB vehicle_data.json
const ALL_BRANDS = [
  'Aprilia', 'Ather', 'Audi', 'BYD', 'Bajaj', 'Benelli', 'BMW', 'BMW Motorrad',
  'CFMoto', 'Citroen', 'Ducati', 'Ford', 'Harley Davidson', 'Hero', 'Hero Electric',
  'Honda', 'Husqvarna', 'Hyundai', 'Jaguar', 'Jawa', 'Jeep', 'Kawasaki', 'Kia',
  'KTM', 'Land Rover', 'Lexus', 'MG', 'Mahindra', 'Maruti Suzuki', 'Mercedes-Benz',
  'Mini', 'Nissan', 'Okinawa', 'Ola Electric', 'Piaggio', 'Porsche', 'Renault',
  'Revolt', 'Royal Enfield', 'Simple Energy', 'Skoda', 'Suzuki', 'TVS',
  'TVS iQube', 'Tata', 'Toyota', 'Triumph', 'Ultraviolette', 'Vida', 'Volkswagen',
  'Volvo', 'Yamaha', 'Yezdi',
];

// Highest-demand brands surface first (9 bike + 9 car); the rest reveal on expand.
const POPULAR_BRANDS = [
  'Hero', 'Honda', 'Bajaj', 'Royal Enfield', 'TVS', 'Yamaha', 'Suzuki', 'KTM', 'Ola Electric',
  'Maruti Suzuki', 'Hyundai', 'Tata', 'Mahindra', 'Kia', 'Toyota', 'Volkswagen', 'Skoda', 'Renault',
];
const ORDERED_BRANDS = [
  ...POPULAR_BRANDS,
  ...ALL_BRANDS.filter((b) => POPULAR_BRANDS.indexOf(b) === -1).sort((a, b) => a.localeCompare(b)),
];

// Highest-demand cities surface first; the rest (alphabetical) reveal on expand.
const TOP_CITIES = [
  'Bengaluru', 'Delhi', 'Mumbai', 'Pune', 'Hyderabad', 'Chennai',
  'Gurugram', 'Noida', 'Ghaziabad', 'Jaipur', 'Lucknow', 'Ahmedabad',
];

// High-intent doorstep searches — homepage only.
const POPULAR_SEARCHES = [
  { label: 'Mechanic Near Me', href: '/mechanic-near-me' },
  { label: 'Bike Service Near Me', href: '/bike-service-near-me' },
  { label: 'Car Service Near Me', href: '/car-service-near-me' },
  { label: 'Bike Repair Near Me', href: '/bike-repair-near-me' },
  { label: 'Car Repair Near Me', href: '/car-repair-near-me' },
  { label: 'Car AC Repair Near Me', href: '/car-ac-repair-near-me' },
  { label: 'Car Battery Replacement Near Me', href: '/car-battery-replacement-near-me' },
  { label: 'Bike Puncture Repair Near Me', href: '/bike-puncture-repair-near-me' },
  { label: 'Bike Wash Near Me', href: '/bike-wash-near-me' },
];

/**
 * Pill link group with collapse/expand. Every link ships in the SSR DOM
 * (crawlable); pills past `visible` are hidden by CSS until expanded, so
 * large groups (Hyderabad has 249 areas) never render as a link wall.
 */
class PillGroup extends Component {
  state = { expanded: false };

  render() {
    const { title, links, visible = 18 } = this.props;
    const { expanded } = this.state;
    if (!links || !links.length) return null;
    const overflow = links.length - visible;

    return (
      <div className={`rr-nethub__group${expanded ? ' is-expanded' : ''}`}>
        <h3 className="rr-nethub__group-title">{title}</h3>
        <div className="rr-nethub__pills">
          {links.map((l, i) => (
            <Link
              key={l.href}
              to={l.href}
              className={`rr-nethub__pill${i >= visible ? ' rr-nethub__pill--extra' : ''}`}
            >
              {l.label}
            </Link>
          ))}
          {overflow > 0 && (
            <button
              type="button"
              className="rr-nethub__toggle"
              aria-expanded={expanded}
              onClick={() => this.setState({ expanded: !expanded })}
            >
              {expanded ? 'Show less' : `Show all ${links.length}`}
            </button>
          )}
        </div>
      </div>
    );
  }
}

class PartnerShipsSection extends Component {
  render() {
    const { cityKey = '', cityContent = {} } = this.props;

    const formattedCityKey = cityKey
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    const brandLinks = ORDERED_BRANDS.map((brand) => ({
      label: brand,
      href: `/brands/${slugify(brand)}`,
    }));

    // City pages surface their areas; the homepage surfaces every city.
    let placeTitle = 'Cities We Serve';
    let placeLinks = [];
    if (cityKey && cityContent[cityKey] && cityContent[cityKey].areas) {
      placeTitle = `Areas We Serve in ${formattedCityKey}`;
      placeLinks = [...cityContent[cityKey].areas]
        .sort((a, b) => a.label.localeCompare(b.label))
        .map((areaObj) => ({ label: areaObj.label, href: `/city/${cityKey}/${areaObj.value}` }));
    } else {
      const rest = cities
        .filter((c) => TOP_CITIES.indexOf(c.name) === -1)
        .sort((a, b) => a.name.localeCompare(b.name));
      const ordered = [
        ...TOP_CITIES.map((name) => cities.find((c) => c.name === name)).filter(Boolean),
        ...rest,
      ];
      placeLinks = ordered.map((city) => ({ label: city.name, href: `/city/${slugify(city.name)}` }));
    }

    return (
      <SectionWrapper
        variant="gray"
        kicker="Our Network"
        heading="Doorstep Service Across India"
        subtitle="Trusted mechanics for every major brand — at your doorstep in 30+ cities."
        className="rr-nethub"
      >
        <PillGroup title={placeTitle} links={placeLinks} visible={cityKey ? 18 : 12} />
        <PillGroup title="Popular Brands" links={brandLinks} visible={18} />
        {!cityKey && <PillGroup title="Popular Searches" links={POPULAR_SEARCHES} visible={9} />}
      </SectionWrapper>
    );
  }
}

export default PartnerShipsSection;
